import React, { useEffect, useState } from 'react';
import { View } from 'react-native';
import { translate } from '../../services/translate';
import { WindowInformation } from '../../services/window-information';
import FormInput from '../FormInput';
import { appAgentService } from '../../services/central-api/app-agent';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import BasicModal from '../BasicModal';
import FilterDropdown from '../Filter/components/FilterDropdown';

interface InvolvedObjectModalParameters {
    involvedObject: InvolvedObject;
    setInvolvedObject: (value: InvolvedObject) => void;
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
    onSubmit: () => void;
}

export default function InvolvedObjectModal({ involvedObject, setInvolvedObject, isModalVisible, setModalVisible, onSubmit }: InvolvedObjectModalParameters) {
    const { styles } = useStyles(styleSheet);

    const windowInfo = WindowInformation();

    const [selectableObjectsCategories, setSelectableObjectsCategories] = useState<ObjectCategory[]>([]);
    const [selectableObjectSubCategories, setSelectableObjectSubCategories] = useState<ObjectSubCategory[]>([]);

    const [amount, setAmount] = useState<string>('0');

    async function loadPageInfo() {
        const objectCategories = await appAgentService.getSimplifiedObjectCategories();
        setSelectableObjectsCategories(objectCategories);
        setAmount(String(involvedObject.amount));
        if (involvedObject.objectCategoryId) {
            await getObjectSubCategories(involvedObject.objectCategoryId);
        }
    }

    async function getObjectSubCategories(objectCategoryId: number) {
        const objectSubCategories = await appAgentService.getSimplifiedObjectSubCategories(objectCategoryId);
        setSelectableObjectSubCategories(objectSubCategories);
    }

    useEffect(() => {
        involvedObject.amount = Number(amount);
    }, [amount]);

    function isValidForm() {
        if (amount.trim() === '' || Number.isNaN(Number(amount)) || !Number(amount)) {
            return false;
        }
        if (involvedObject.objectCategoryId === 0) {
            return false;
        }
        if (!involvedObject.objectSubCategoryId) {
            return false;
        }
        return true;
    }

    useEffect(() => {
        loadPageInfo();
    }, [isModalVisible]);

    return (
        <BasicModal
            isModalVisible={isModalVisible}
            setModalVisible={setModalVisible}
            width='extra-large'
            title={translate('involvedObject')}
            primaryButton={{
                text: translate('save'),
                onClick: onSubmit,
                disabled: !isValidForm(),
            }}
        >
            <View style={{ rowGap: 20 }}>
                <View style={[styles.row, { zIndex: 4 }]}>
                    <View style={[{ gap: 5, zIndex: 3 }, windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }]}>
                        <FilterDropdown
                            label={translate('vehicle_metadata_category')}
                            items={selectableObjectsCategories.map((objectCategory) => {
                                return {
                                    value: objectCategory.id,
                                    label: objectCategory.name
                                };
                            })}
                            zIndex={3}
                            value={involvedObject.objectCategoryId}
                            setValue={async (value) => {
                                const objectCategoryId = value(involvedObject.objectCategoryId);
                                setInvolvedObject({
                                    ...involvedObject,
                                    objectCategoryId,
                                    objectCategory: selectableObjectsCategories.find(objectCategory => objectCategory.id == objectCategoryId)
                                });
                                await getObjectSubCategories(value(involvedObject.objectCategoryId));
                            }}
                            required
                        />
                    </View>
                    <View style={[{ gap: 5, zIndex: 2 }, windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }]}>
                        <FilterDropdown
                            label={translate('subCategory')}
                            items={selectableObjectSubCategories.map((objectSubCategory) => {
                                return {
                                    value: objectSubCategory.id,
                                    label: objectSubCategory.name
                                };
                            })}
                            value={involvedObject.objectSubCategoryId || null}
                            setValue={(value) => {
                                const objectSubCategoryId = value(involvedObject.objectSubCategoryId);
                                setInvolvedObject({
                                    ...involvedObject,
                                    objectSubCategoryId,
                                    objectSubCategory: selectableObjectSubCategories.find(objectSubCategory => objectSubCategory.id == objectSubCategoryId)
                                });
                            }}
                            zIndex={2}
                            required
                        />
                    </View>
                    <FormInput
                        label={translate('amount')}
                        placeholder={translate('amount')}
                        value={amount}
                        onChangeText={setAmount}
                        invalid={() => false}
                        viewStyle={[{ flex: 1 }, windowInfo.isMobileOrTablet ? { width: '100%' } : { width: '30%' }]}
                        required
                    />
                </View>
                <View style={styles.row}>
                    <FormInput
                        label={translate('description')}
                        placeholder={translate('description')}
                        value={involvedObject.description ?? ''}
                        onChangeText={(value) => setInvolvedObject({ ...involvedObject, description: value })}
                        invalid={() => false}
                        viewStyle={{ flex: 1 }}
                    />
                </View>
            </View>
        </BasicModal>
    );
}

const styleSheet = createStyleSheet(() => ({
    row: {
        flex: 1,
        flexDirection: 'row',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
        columnGap: 30,
        minHeight: 'auto',
        rowGap: 20,
    },
}));
